<template>
  <div>
    <page-header
      title="数据修复"
      :crumbs="['投后管理', '数据修复']"
      subtitle="重新抓取历史营收数据，比对差异并生成修复记录"
    />

    <!-- 筛选区 -->
    <div class="filter-card">
      <div class="filter-bar">
        <div class="filter-inline">
          <label>品牌</label>
          <el-select
            v-model="filterForm.brandCode"
            placeholder="全部品牌"
            clearable
            style="width: 160px"
            @change="onBrandChange"
          >
            <el-option v-for="b in brandOptions" :key="b.brandCode" :label="b.brandName" :value="b.brandCode" />
          </el-select>
        </div>
        <div class="filter-inline">
          <label>门店</label>
          <el-select
            v-model="filterForm.storeEntCodes"
            multiple
            collapse-tags
            collapse-tags-tooltip
            placeholder="全部门店"
            style="width: 280px"
          >
            <el-option v-for="s in storeList" :key="s.storeEntCode" :label="s.storeName" :value="s.storeEntCode" />
          </el-select>
        </div>
        <div class="filter-inline">
          <label>时间范围</label>
          <el-date-picker
            v-model="filterForm.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="YYYY-MM-DD"
            style="width: 250px"
          />
        </div>
        <button class="btn btn-primary" :disabled="loading" @click="refreshData">
          {{ loading ? '抓取中…' : '重新抓取数据' }}
        </button>
      </div>
    </div>

    <!-- 结果区 -->
    <template v-if="fixResult">
      <div class="summary-bar">
        <div class="summary-item">
          <div class="summary-label">原营业额总计</div>
          <div class="summary-value num">{{ fixResult.totalOldAmount ?? '—' }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">新营业额总计</div>
          <div class="summary-value num">{{ fixResult.totalNewAmount ?? '—' }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">差额总计</div>
          <div class="summary-value num" :class="diffClass(fixResult.totalDifference)">
            {{ fixResult.totalDifference ?? '—' }}
          </div>
        </div>
      </div>

      <DataTable
        :columns="columns"
        :data="fixResult.rows || []"
        :total="0"
        row-key="storeEntCode"
        empty-text="本次抓取未发现数据"
      >
        <template #toolbar-left>
          <span class="dt-hint">
            历史数据差异对比 · 共 {{ (fixResult.rows || []).length }} 行
            <b v-if="hasDiff">需确认</b>
          </span>
        </template>

        <template #toolbar-actions>
          <button v-if="hasDiff" class="dt-tool is-danger" @click="confirmFix">确认差异</button>
        </template>

        <template #cell-difference="{ row }">
          <span class="num" :class="diffClass(row.difference)">{{ row.difference }}</span>
        </template>
      </DataTable>
    </template>

    <div v-else class="empty-card">
      <p>选择品牌、门店与时间范围后，点击「重新抓取数据」开始比对</p>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import PageHeader from '@/components/PageHeader.vue'
import DataTable from '@/components/DataTable.vue'
import assetService from '@/services/assetService'
import postInvestService from '@/services/postInvestService'

const loading = ref(false)
const fixResult = ref(null)

const filterForm = reactive({
  brandCode: '',
  storeEntCodes: [],
  dateRange: []
})

const brandOptions = ref([])
const storeList = ref([])

const columns = [
  { prop: 'storeEntCode', label: '门店编码', minWidth: 140 },
  { prop: 'storeName', label: '门店名称', minWidth: 160 },
  { prop: 'incomeDate', label: '日期', minWidth: 120 },
  { prop: 'oldAmount', label: '原营业额', minWidth: 120, type: 'number' },
  { prop: 'newAmount', label: '新营业额', minWidth: 120, type: 'number' },
  { prop: 'difference', label: '差额', minWidth: 120, type: 'number' }
]

const hasDiff = computed(() => !!fixResult.value && fixResult.value.totalDifference !== 0)

// 差额：新比原高视为「增」，遵循涨红跌绿
const diffClass = (v) => {
  if (v === null || v === undefined || v === '' || Number(v) === 0) return ''
  return Number(v) > 0 ? 'up' : 'down'
}

const loadBrands = async () => {
  try {
    const res = await assetService.getBrandList({})
    brandOptions.value = res?.data?.list || res?.data || []
  } catch (e) {
    ElMessage.error(e?.message || '获取品牌列表失败')
  }
}

const onBrandChange = async () => {
  filterForm.storeEntCodes = []
  if (!filterForm.brandCode) {
    storeList.value = []
    return
  }
  try {
    const res = await assetService.getStoreListByBrandCode({ brandCode: filterForm.brandCode })
    storeList.value = res?.data || []
  } catch (e) {
    ElMessage.error(e?.message || '获取门店列表失败')
  }
}

const refreshData = async () => {
  if (!filterForm.dateRange || filterForm.dateRange.length < 2) {
    ElMessage.warning('请选择时间范围')
    return
  }
  loading.value = true
  try {
    const res = await postInvestService.refreshIncomeData({
      brandCode: filterForm.brandCode,
      storeEntCodes: filterForm.storeEntCodes,
      startDate: filterForm.dateRange[0],
      endDate: filterForm.dateRange[1]
    })
    fixResult.value = res?.data
    ElMessage.success('数据重新抓取完成')
  } catch (e) {
    ElMessage.error(e?.message || '抓取失败')
  } finally {
    loading.value = false
  }
}

// 说明：原实现误用了 ElMessage.confirm（该方法不存在），
// 确认弹窗应使用 ElMessageBox.confirm
const confirmFix = async () => {
  if (!fixResult.value) return
  try {
    await ElMessageBox.confirm(
      `确认差额为 ${fixResult.value.totalDifference}，将插入修复记录？`,
      '确认差异',
      { confirmButtonText: '确认', cancelButtonText: '取消', type: 'warning' }
    )
  } catch {
    return
  }

  try {
    await postInvestService.confirmDataFix({
      brandCode: filterForm.brandCode,
      storeEntCodes: filterForm.storeEntCodes,
      startDate: filterForm.dateRange[0],
      endDate: filterForm.dateRange[1],
      difference: fixResult.value.totalDifference
    })
    ElMessage.success('修复记录已插入')
    fixResult.value = null
  } catch (e) {
    ElMessage.error(e?.message || '确认失败')
  }
}

loadBrands()
</script>

<style scoped>
.filter-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 12px 16px;
  margin-bottom: 12px;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-inline {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-inline label {
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
}

.btn {
  padding: 6px 14px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  background: var(--bg-card);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}

.btn:hover:not(:disabled) {
  border-color: var(--brand-500);
  color: var(--brand-500);
}

.btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--brand-500);
  border-color: var(--brand-500);
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: var(--brand-600);
  border-color: var(--brand-600);
  color: #fff;
}

.summary-bar {
  display: flex;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 13px 18px;
  margin-bottom: 12px;
}

.summary-item {
  flex: 1;
}

.summary-item + .summary-item {
  padding-left: 18px;
  border-left: 1px solid var(--border);
}

.summary-label {
  font-size: 11px;
  color: var(--text-tertiary);
}

.summary-value {
  font-size: 18px;
  font-weight: 500;
  margin-top: 3px;
}

.dt-hint {
  font-size: 12px;
  color: var(--text-secondary);
}

.dt-hint b {
  margin-left: 6px;
  color: var(--danger-text);
  font-weight: 500;
}

.dt-tool {
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 12px;
  color: var(--text-secondary);
  cursor: pointer;
}

.dt-tool:hover {
  color: var(--brand-500);
}

.dt-tool.is-danger {
  color: var(--danger-text);
}

.dt-tool.is-danger:hover {
  text-decoration: underline;
}

.empty-card {
  background: var(--bg-card);
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-card);
  padding: 48px 20px;
  text-align: center;
}

.empty-card p {
  margin: 0;
  font-size: 13px;
  color: var(--text-tertiary);
}
</style>
